/**
 * Shell orchestration: landing -> gate -> play.
 *
 *   landing  first run only. Import game data into the vault.
 *   gate     one click. Satisfies the autoplay policy before any audio starts.
 *   play     engine boots full-viewport; HUD and save archive attach.
 *
 * Returning visits skip straight to the gate. "Manage game data" is reachable
 * from the gate and is the only route back to the installer.
 */

import './styles.css';
import {
  DEFAULT_DISPLAY_MODE,
  DEFAULT_SETTINGS,
  applyDebugMode,
  applyDisplayMode,
  currentViewport,
  f1ResIni,
  f2ResIni,
  fallout2Cfg,
  falloutCfg,
  resolveDisplayMode,
  setRuntimeGame,
  type DisplayMode,
  type EngineSettings,
  type GameType,
} from './config';
import { assessStorage, countPersistedSlots } from './durability';
import { boot, enableCanvasReadback, type BootedEngine, type EmFS } from './engine';
import { installPwaMetadata } from './pwa';
import { readRegistry, registerGame, getActiveGame, setActiveGame, type GameEntry } from './registry';
import { getMeta, setMeta, useVault, type InstallMeta } from './store';
import { acknowledgeUnpatched, isUnpatchedAcknowledged, purgeContamination } from './import';
import { configureSaveTopology, listSlots, recoverSaveTransactions, slotsSignature } from './saves';
import { $, bytes, showScreen, toast, toastAction } from './ui/dom';
import { isDevArena, isDevDirectMount, isDevGameData, isDevSimulator, mountInstaller } from './ui/installer';
import { mountHud, type Hud } from './ui/hud';
import { mountManage } from './ui/manage';
import { mountA2hs, type A2hsChip } from './ui/a2hs';
import { mountRecoveryNotice, mountRestoreLane } from './ui/restore-lane';
import { mountSaveArchive } from './ui/saves';
import { mountSaveEditor, type BridgeStatus, type SaveEditorPanel } from './ui/saveedit';
import { resolveSaveEditBridge, resolveSaveTopology } from './saveedit/bridge';
import { mountPatchPanel, type PatchPanel } from './ui/patch';
import { mountRpuPanel, type RpuPanel } from './ui/rpu';
import { applyRecipesToBoot } from './mods/manager';
import { mountModMaker, type ModMakerPanel } from './ui/modmaker';
import { getRpuMeta } from './import/rpu';
import { mountSettings, type SettingsPanel } from './ui/settings';
import { mountTextEntry, type TextEntryBridge } from './ui/textentry';
import { mountTelemetry, type Telemetry } from './ui/telemetry';

// Must run before SDL ever asks for a WebGL context or development screenshots
// come back blank. Cheap, idempotent, so do it at module load.
enableCanvasReadback();

if (isDevSimulator()) {
  document.documentElement.classList.add('simulator-route');
  const loader = document.createElement('div');
  loader.id = 'simulator-loader';
  loader.innerHTML = '<strong>F2CE COMBAT SIMULATOR</strong><span>Starting the rules engine…</span>';
  document.body.append(loader);
}

const HITBOX_DEBUG = new URLSearchParams(location.search).has('hitboxes');

type TouchDebugPointer = { phase: string; x: number; y: number; at: number };
const touchDebugPointers: TouchDebugPointer[] = [];

let engine: BootedEngine | null = null;
let lastSlotsSignature = '';
let settingsPanel: SettingsPanel | null = null;
let activeHud: Hud | null = null;
let patchPanel: PatchPanel | null = null;
let rpuPanel: RpuPanel | null = null;
let modMaker: ModMakerPanel | null = null;
/** Mirrors the persisted preference so the START click — the one user gesture
 *  a fullscreen request is allowed to ride — does not have to await IndexedDB. */
let fullscreenPref = false;
/** Set once the engine has stopped — exited, crashed, or never booted at all.
 *  The shell drops fullscreen itself at that point, and that drop is the shell's
 *  doing: not a preference change, and not a reason to offer a restart. */
let engineStopped = false;
let activeGameDisplayMode: DisplayMode | null = null;
let touchAssistScale = 100;
/** The modality of the most recent pointer input, hoisted out of the canvas
 *  forwarder so the text-entry bridge can ask the same question. Seeded from
 *  the media query, because a phone that has not been tapped yet is still a
 *  phone and its first text prompt still needs a keyboard. */
let lastInputTouchLike = matchMedia('(pointer: coarse)').matches;
/** Mounted once, on the first successful boot. Restart paths reload the page,
 *  but the guard keeps a second mount from ever stacking two bars. */
let textEntry: TextEntryBridge | null = null;
/** Recording overlay. Always constructed; shows nothing unless asked for. */
let telemetry: Telemetry | null = null;

/** Fullscreen belongs to the running game; leaving it on after the engine is
 *  gone strands the player in a borderless shell with nothing playing. */
function releaseFullscreen(): void {
  engineStopped = true;
  if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
}

function startTouchPreview(canvas: HTMLCanvasElement): void {
  const overlay = $<HTMLCanvasElement>('#touch-preview');
  const ctx = overlay.getContext('2d');
  if (!ctx) return;

  const draw = () => {
    if (engineStopped) return;
    const module = engine?.module;
    const displayMode = activeGameDisplayMode;
    const kind = module?._rr_touch_preview_kind?.() ?? 0;
    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelWidth = Math.max(1, Math.round(width * dpr));
    const pixelHeight = Math.max(1, Math.round(height * dpr));
    if (overlay.width !== pixelWidth) overlay.width = pixelWidth;
    if (overlay.height !== pixelHeight) overlay.height = pixelHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    if (kind !== 0 && displayMode) {
      const sourceRect = canvas.getBoundingClientRect();
      const scaleX = sourceRect.width / displayMode.width;
      const scaleY = sourceRect.height / displayMode.height;
      const mapX = (x: number) => sourceRect.left + x * scaleX;
      const mapY = (y: number) => sourceRect.top + y * scaleY;
      const presentation = $('#game-presentation');
      ctx.save();
      if (presentation.classList.contains('mobile-hud-active')) {
        const clip = $('#world-clip').getBoundingClientRect();
        ctx.beginPath();
        ctx.rect(clip.left, clip.top, clip.width, clip.height);
        ctx.clip();
      }

      const pathCount = Math.min(64, Math.max(0, module?._rr_touch_preview_path_count?.() ?? 0));
      if (pathCount > 0 && module?._rr_touch_preview_path_x && module?._rr_touch_preview_path_y) {
        ctx.strokeStyle = kind === 3 ? 'rgba(255, 190, 45, .95)' : 'rgba(45, 225, 255, .95)';
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        for (let index = 0; index < pathCount; index += 1) {
          const x = mapX(module._rr_touch_preview_path_x(index));
          const y = mapY(module._rr_touch_preview_path_y(index));
          if (index === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      const targetX = mapX(module?._rr_touch_preview_target_x?.() ?? 0);
      const targetY = mapY(module?._rr_touch_preview_target_y?.() ?? 0);
      const colors = ['#fff', '#ff5d5d', '#36e2ff', '#ffbf38', '#ffe36a', '#ffe36a', '#ffe36a', '#ff5d5d'];
      const labels = ['', 'ATTACK', 'MOVE', 'MOVE TO ATTACK', 'PICK UP', 'USE', 'LOOT', 'BLOCKED'];
      const color = colors[kind] ?? '#fff';
      ctx.strokeStyle = color;
      ctx.fillStyle = `${color}33`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(targetX, targetY, 19, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      const ap = module?._rr_touch_preview_action_points?.() ?? -1;
      const label = `${labels[kind] ?? 'ACTION'}${ap >= 0 ? ` · ${ap} AP` : ''}`;
      ctx.font = 'bold 12px ui-monospace, monospace';
      const textWidth = ctx.measureText(label).width;
      const labelX = Math.max(6, Math.min(width - textWidth - 14, targetX - textWidth / 2 - 7));
      const labelY = Math.max(22, targetY - 27);
      ctx.fillStyle = 'rgba(0, 0, 0, .82)';
      ctx.fillRect(labelX, labelY - 16, textWidth + 14, 21);
      ctx.fillStyle = color;
      ctx.fillText(label, labelX + 7, labelY);
      ctx.restore();
    }

    requestAnimationFrame(draw);
  };
  requestAnimationFrame(draw);
}

const getFS = (): EmFS | null => engine?.FS ?? null;

/** Reads the engine's text-prompt prefill: a NUL-terminated C string in a
 *  256-byte static. Engine strings are single-byte, so latin1 round-trips them
 *  exactly where UTF-8 would mangle the high range. */
const readTextInputPrefill = (): string => {
  const ptr = engine?.module._rr_text_input_prefill?.() ?? 0;
  const heap = engine?.module.HEAPU8;
  if (ptr <= 0 || !heap) return '';
  let end = ptr;
  const limit = Math.min(ptr + 256, heap.length);
  while (end < limit && heap[end] !== 0) end++;
  return new TextDecoder('latin1').decode(heap.subarray(ptr, end));
};

function startHitboxDebug(canvas: HTMLCanvasElement): void {
  if (!HITBOX_DEBUG) return;

  const overlay = $<HTMLCanvasElement>('#hitbox-debug');
  const ctx = overlay.getContext('2d');
  if (!ctx) return;
  overlay.classList.add('enabled');
  let lastGestureSequence = -1;
  const gestures: Array<{ label: string; x: number; y: number; startX: number; startY: number }> = [];
  let lastTapSequence = -1;
  let lastTapStages = -1;
  const tapTraces: Array<{ sequence: number; stages: number; label: string; detail: string }> = [];

  const draw = () => {
    if (engineStopped) return;
    const module = engine?.module;
    const countHitboxes = module?._rr_debug_hitbox_count;
    const leftAt = module?._rr_debug_hitbox_left;
    const topAt = module?._rr_debug_hitbox_top;
    const rightAt = module?._rr_debug_hitbox_right;
    const bottomAt = module?._rr_debug_hitbox_bottom;
    const isInterfaceAt = module?._rr_debug_hitbox_is_interface;
    const displayMode = activeGameDisplayMode;
    if (!countHitboxes || !leftAt || !topAt || !rightAt || !bottomAt || !displayMode) {
      requestAnimationFrame(draw);
      return;
    }

    const dpr = window.devicePixelRatio || 1;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const pixelWidth = Math.max(1, Math.round(width * dpr));
    const pixelHeight = Math.max(1, Math.round(height * dpr));
    if (overlay.width !== pixelWidth) overlay.width = pixelWidth;
    if (overlay.height !== pixelHeight) overlay.height = pixelHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const hitboxes: Array<{ left: number; top: number; right: number; bottom: number; isInterface: boolean }> = [];
    const count = Math.min(256, Math.max(0, countHitboxes()));
    for (let index = 0; index < count; index++) {
      const left = leftAt(index);
      const top = topAt(index);
      const right = rightAt(index);
      const bottom = bottomAt(index);
      if (left >= 0 && top >= 0 && right >= left && bottom >= top) {
        hitboxes.push({ left, top, right, bottom, isInterface: isInterfaceAt?.(index) === 1 });
      }
    }

    const drawMapped = (
      hitbox: (typeof hitboxes)[number],
      originX: number,
      originY: number,
      scaleX: number,
      scaleY: number
    ) => {
      const x = originX + hitbox.left * scaleX;
      const y = originY + hitbox.top * scaleY;
      const boxWidth = (hitbox.right - hitbox.left + 1) * scaleX;
      const boxHeight = (hitbox.bottom - hitbox.top + 1) * scaleY;
      const logicalWidth = hitbox.right - hitbox.left + 1;
      const logicalHeight = hitbox.bottom - hitbox.top + 1;
      if (logicalWidth <= 32 && logicalHeight <= 32) {
        // Match the engine's label-row touch assist for tiny red lamps. Yellow
        // now shows the real effective target instead of the older radius.
        const horizontalReach = Math.max(120, displayMode.width * 160 / 640);
        const verticalPadding = Math.max(7, displayMode.height * 7 / 480);
        ctx.fillStyle = 'rgba(255, 210, 0, 0.13)';
        ctx.fillRect(x - horizontalReach * scaleX, y - verticalPadding * scaleY,
          boxWidth + horizontalReach * scaleX * 2, boxHeight + verticalPadding * scaleY * 2);
      }
      ctx.fillStyle = 'rgba(255, 45, 45, 0.22)';
      ctx.strokeStyle = 'rgba(255, 90, 90, 0.9)';
      ctx.lineWidth = 1;
      ctx.fillRect(x, y, boxWidth, boxHeight);
      ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, boxWidth - 1), Math.max(0, boxHeight - 1));
    };

    const sourceRect = canvas.getBoundingClientRect();
    const sourceScaleX = sourceRect.width / displayMode.width;
    const sourceScaleY = sourceRect.height / displayMode.height;
    const presentation = $('#game-presentation');
    if (presentation.classList.contains('mobile-hud-active')) {
      const clip = $('#world-clip').getBoundingClientRect();
      ctx.save();
      ctx.beginPath();
      ctx.rect(clip.left, clip.top, clip.width, clip.height);
      ctx.clip();
      hitboxes.forEach((box) => drawMapped(box, sourceRect.left, sourceRect.top, sourceScaleX, sourceScaleY));
      ctx.restore();

      const hudRect = $<HTMLCanvasElement>('#mobile-hud-bar').getBoundingClientRect();
      const barX = Math.max(0, (displayMode.width - 640) / 2);
      const barY = displayMode.height - 100;
      for (const box of hitboxes) {
        if (!box.isInterface || box.top < barY || box.left < barX || box.right > barX + 639) continue;
        drawMapped(
          { left: box.left - barX, right: box.right - barX, top: box.top - barY, bottom: box.bottom - barY, isInterface: true },
          hudRect.left,
          hudRect.top,
          hudRect.width / 640,
          hudRect.height / 100
        );
      }
      if (presentation.classList.contains('mobile-map-active')) {
        ctx.fillStyle = 'rgba(30, 155, 255, 0.18)';
        ctx.strokeStyle = 'rgba(70, 190, 255, 0.95)';
        const x = hudRect.left + hudRect.width * 570 / 640;
        const y = hudRect.top + hudRect.height * 32 / 100;
        const w = hudRect.width * 70 / 640;
        const h = hudRect.height * 64 / 100;
        ctx.fillRect(x, y, w, h);
        ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
      }
    } else {
      hitboxes.forEach((box) => drawMapped(box, sourceRect.left, sourceRect.top, sourceScaleX, sourceScaleY));
    }

    const gestureSequence = module._rr_debug_touch_sequence?.() ?? -1;
    if (gestureSequence !== -1 && gestureSequence !== lastGestureSequence) {
      lastGestureSequence = gestureSequence;
      const type = module._rr_debug_touch_type?.() ?? 0;
      const state = module._rr_debug_touch_state?.() ?? 0;
      const typeName = ['NONE', 'TAP', 'HOLD', 'PAN'][type] ?? `TYPE${type}`;
      const stateName = ['POSSIBLE', 'BEGIN', 'MOVE', 'END'][state] ?? `STATE${state}`;
      gestures.unshift({
        label: `${gestureSequence} ${typeName}/${stateName}`,
        x: module._rr_debug_touch_x?.() ?? 0,
        y: module._rr_debug_touch_y?.() ?? 0,
        startX: module._rr_debug_touch_start_x?.() ?? 0,
        startY: module._rr_debug_touch_start_y?.() ?? 0,
      });
      if (gestures.length > 6) gestures.pop();
    }

    const latestGesture = gestures[0];
    if (latestGesture) {
      const startX = sourceRect.left + latestGesture.startX * sourceScaleX;
      const startY = sourceRect.top + latestGesture.startY * sourceScaleY;
      const endX = sourceRect.left + latestGesture.x * sourceScaleX;
      const endY = sourceRect.top + latestGesture.y * sourceScaleY;
      ctx.strokeStyle = 'rgba(0, 255, 235, 0.95)';
      ctx.fillStyle = 'rgba(0, 255, 235, 0.28)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(endX, endY, 12, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    const latestPointer = touchDebugPointers[0];
    const scrollSequence = module._rr_debug_scroll_sequence?.() ?? 0;
    const scrollBefore = module._rr_debug_scroll_before?.() ?? -1;
    const scrollAfter = module._rr_debug_scroll_after?.() ?? -1;
    const scrollLane = module._rr_debug_scroll_lane?.() ?? -1;
    const scrollPhase = module._rr_debug_scroll_phase?.() ?? -1;
    const scrollQueued = module._rr_debug_scroll_queued?.() ?? 0;
    const scrollConsumed = module._rr_debug_scroll_consumed?.() ?? 0;
    const scrollLine = scrollSequence > 0
      ? `SCROLL ${scrollSequence} keys ${module._rr_debug_scroll_up_key?.() ?? -1}/${module._rr_debug_scroll_down_key?.() ?? -1}`
        + (scrollBefore >= 0 ? ` offset ${scrollBefore}→${scrollAfter}` : ' BOUND')
      : 'SCROLL waiting';
    const scrollRawLine = scrollLane >= 0
      ? `RAIL lane ${scrollLane + 1}/4 phase ${scrollPhase} queued ${scrollQueued} used ${scrollConsumed}`
      : 'RAIL waiting';
    const perkLine = `PERK emit ${module._rr_debug_scroll_plain_emits?.() ?? -1} eat ${module._rr_debug_perk_arrows_consumed?.() ?? -1}`;
    const tapSequence = module._rr_debug_tap_sequence?.() ?? 0;
    const tapStages = module._rr_debug_tap_stages?.() ?? 0;
    if (tapSequence > 0 && (tapSequence !== lastTapSequence || tapStages !== lastTapStages)) {
      const names: string[] = [];
      if (tapStages & 1) names.push('GEST');
      if (tapStages & 2) names.push('SNAP');
      if (tapStages & 4) names.push('QUEUE');
      if (tapStages & 8) names.push('HOVER');
      if (tapStages & 16) names.push('PRESS');
      if (tapStages & 32) names.push('RELEASE');
      if (tapStages & 64) names.push('EVENT');
      const windowId = module._rr_debug_tap_window?.() ?? -1;
      const buttonId = module._rr_debug_tap_button?.() ?? -1;
      const eventCode = module._rr_debug_tap_event?.() ?? -1;
      const startX = module._rr_debug_tap_start_x?.() ?? 0;
      const startY = module._rr_debug_tap_start_y?.() ?? 0;
      const targetX = module._rr_debug_tap_target_x?.() ?? startX;
      const targetY = module._rr_debug_tap_target_y?.() ?? startY;
      const outcome = buttonId >= 0
        ? `BUTTON w${windowId} b${buttonId}${eventCode !== -1 ? ` e${eventCode}` : ''}`
        : (tapStages & 32) ? 'NO BUTTON/MAP' : 'PENDING';
      const trace = {
        sequence: tapSequence,
        stages: tapStages,
        label: `T${tapSequence} ${names.join('>')} ${outcome}`,
        detail: `${startX},${startY}→${targetX},${targetY}`,
      };
      if (tapTraces[0]?.sequence === tapSequence) tapTraces[0] = trace;
      else tapTraces.unshift(trace);
      if (tapTraces.length > 4) tapTraces.pop();
      lastTapSequence = tapSequence;
      lastTapStages = tapStages;
    }
    ctx.font = '11px ui-monospace, monospace';
    const panelLines = [
      'TOUCH DEBUG ON',
      latestPointer ? `RAW ${latestPointer.phase} ${latestPointer.x},${latestPointer.y}` : 'RAW waiting',
      scrollLine,
      scrollRawLine,
      perkLine,
      ...tapTraces.slice(0, 3).flatMap((trace) => [trace.label, `  ${trace.detail}`]),
      ...gestures.slice(0, 2).map((gesture) => gesture.label),
    ];
    const panelTop = Math.max(96, Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sat')) || 96);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.78)';
    ctx.fillRect(6, panelTop, Math.min(width - 12, 350), panelLines.length * 15 + 10);
    ctx.fillStyle = '#59ff76';
    panelLines.forEach((line, index) => ctx.fillText(line, 12, panelTop + 17 + index * 15));

    requestAnimationFrame(draw);
  };

  requestAnimationFrame(draw);
}

/**
 * One signal for "the saves on disk may have changed".
 *
 * Three things watch for it — the archive list, the Add to Home Screen chip,
 * and (from the backup layer) the automatic vault rewrite — and they arrive at
 * it by different routes: the engine's own syncfs, a shell-side mutation, or
 * the polling watcher. Funnel all of them through here so a new listener does
 * not have to find every route again.
 */
let hasSaves = false;
let a2hsChip: A2hsChip | null = null;

function notifySavesChanged(): void {
  const FS = getFS();
  if (FS) hasSaves = listSlots(FS).some((slot) => slot.occupied);
  saveArchive.refresh();
  a2hsChip?.update();
  window.dispatchEvent(new Event('rerun:saves-changed'));
}

// A shell-side mutation (import/duplicate/delete/edit) has to be pushed to
// IDBFS ourselves; only engine-initiated saves come with a syncfs of their own.
const persistSaves = async (): Promise<void> => {
  if (!engine) throw new Error('the engine filesystem is not mounted');
  await engine.sync();
};

// ------------------------------------------------- the save-editor bridge

/**
 * A bridge round trip has been run against this engine.
 *
 * The engine is not recoverable from one: it ends up holding a loaded game with
 * no main loop and the main menu still allocated (engine PATCHES.md, "What the
 * bridge does not do"). Latched for the life of the page, cleared only by the
 * reload that follows.
 */
let engineSpent = false;

/**
 * What the shell can decide about the bridge without asking the engine.
 *
 * "Is the engine idle at the main menu" is deliberately *not* decided here.
 * There was a heuristic — `data/MAPS` being non-empty as evidence a game had
 * run — and it was wrong: the engine drops an `AUTOMAP.DB` there on the way to
 * the main menu, so it reported "in a game" for every usable engine. The
 * mailbox answers the same question exactly, because only the main-menu loop
 * services it, so `SaveEditBridge.probe()` is the gate and the panel runs it.
 */
function bridgeStatus(): BridgeStatus {
  if (!engine) {
    return { bridge: null, reason: 'the engine has not booted, so there is nothing to ask.' };
  }
  if (engineSpent) {
    return {
      bridge: null,
      reason: 'this engine has already run a save-editor engine session and has no game loop left. Restart the reactor (Quit) first.',
    };
  }
  const bridge = resolveSaveEditBridge(engine.module);
  return bridge
    ? { bridge, reason: null }
    : { bridge: null, reason: 'this engine build does not export it (it predates the save-editor bridge).' };
}

/** The only route back to a playable engine. HUD Quit does exactly this. */
let restartEngine = (): void => location.reload();

let saveEditor: SaveEditorPanel | null = null;

const saveArchive = mountSaveArchive(getFS, persistSaves, (slot) => {
  saveArchive.close();
  saveEditor?.open(slot);
}, () => !(engine?.module._rr_game_active?.() ?? 0));

saveEditor = mountSaveEditor({
  getFS,
  bridgeStatus,
  markEngineSpent: () => {
    engineSpent = true;
  },
  restart: () => restartEngine(),
  persist: async () => void (await persistSaves()),
  onMutate: () => {
    saveArchive.refresh();
  },
});

const managePanel = mountManage(() => {
  window.dispatchEvent(new CustomEvent('rerun:open-intake', { detail: { mode: 'reimport' } }));
});

// ---------------------------------------------------------------- boot screen

function bootProgress(stage: string, done: number, total: number): void {
  $('#boot-stage').textContent = stage.toLowerCase();
  const ratio = total ? done / total : 0;
  $<HTMLElement>('#boot-overlay .bar > i').style.width = `${(ratio * 100).toFixed(1)}%`;
  $('#boot-detail').textContent = total > 1024 ? `${bytes(done)} / ${bytes(total)}` : stage;
  $('#boot-pct').textContent = `${Math.round(ratio * 100)}%`;
}

/**
 * The framebuffer this session should boot with.
 *
 * Sized against the *screen* when the player last played fullscreen, because a
 * reload lands back in a window. The fullscreen request itself rides the START
 * click a moment later.
 */
async function currentDisplayMode() {
  const id = (await getMeta<string>('displayMode')) ?? DEFAULT_DISPLAY_MODE;
  const wantsFullscreen = (await getMeta<boolean>('fullscreen')) ?? false;
  const viewport = currentViewport(wantsFullscreen);
  return mobileDisplayMode(viewport) ?? resolveDisplayMode(id, viewport);
}

function mobileDisplayMode(viewport: ReturnType<typeof currentViewport>): DisplayMode | null {
  if (!matchMedia('(max-width: 600px) and (orientation: portrait)').matches) return null;

  // Mobile has one native render geometry. Desktop's 4:3/Widescreen/Max
  // choices describe monitor framebuffers and create arbitrary crop/letterbox
  // interactions when applied to a portrait phone. Render a 640px-wide world
  // whose height matches the actual map pane; pinch is presentation zoom only.
  const cssWidth = Math.max(1, viewport.deviceW / viewport.dpr);
  const cssHeight = Math.max(1, viewport.deviceH / viewport.dpr);
  const fixedChromeHeight = 90;
  const mirroredHudHeight = cssWidth * 100 / 640;
  const worldCssHeight = Math.max(380, cssHeight - fixedChromeHeight - mirroredHudHeight);
  const rawHeight = 100 + worldCssHeight * 640 / cssWidth;
  const height = Math.min(1200, Math.max(480, Math.floor(rawHeight / 2) * 2));
  return { id: 'mobile', label: `Mobile — 640 × ${height}`, width: 640, height };
}

async function currentCfgText(gameType: GameType, mode: DisplayMode): Promise<string> {
  const cfgKey = gameType === 'fo1' ? 'fallout.cfg' : 'fallout2.cfg';
  // Prefer the config the engine itself last wrote: it carries the player's
  // volume, difficulty and combat preferences. We only force our own knobs.
  const stored = await getMeta<string>(cfgKey);
  const settings = (await getMeta<EngineSettings>('settings')) ?? DEFAULT_SETTINGS;
  const played = await getMeta<boolean>('hasPlayed');
  let base = stored ?? (gameType === 'fo1' ? falloutCfg(settings) : fallout2Cfg(settings));
  if (gameType === 'fo2') {
    // The engine rewrites fallout2.cfg on exit with whatever window size it
    // ended up with, so the display choice has to be re-stamped on every boot
    // or it silently reverts.
    base = applyDisplayMode(base, mode);
  }
  // A stored cfg from before we switched to mode=screen would keep discarding
  // every debugPrint, so force it too.
  base = applyDebugMode(base);
  if (!played) return base;
  // FO2 only: skip intro movies after first run
  if (gameType === 'fo2') {
    return base.includes('skip_opening_movies=')
      ? base.replace(/skip_opening_movies=\d+/, 'skip_opening_movies=2')
      : base.replace(/\[ui\]/, '[ui]\nskip_opening_movies=2');
  }
  return base;
}

/** Stage a resize for the engine's next safe point and wait for completion. */
async function applyLiveDisplayMode(id: string): Promise<void> {
  const module = engine?.module;
  const request = module?._rr_resize_request;
  const status = module?._rr_resize_status;
  if (!module || !request || !status) {
    const message = 'This engine build does not expose live resolution changes.';
    toast(message, 'bad');
    throw new Error(message);
  }

  const viewport = currentViewport();
  const mode = mobileDisplayMode(viewport) ?? resolveDisplayMode(id, viewport);
  const accepted = request(mode.width, mode.height);
  if (accepted !== 0) {
    const message = accepted === -2 ? 'A resolution change is already in progress.' : 'The engine rejected that resolution.';
    toast(message, 'bad');
    throw new Error(message);
  }

  // A modal loop (most importantly combat and its animations) can own engine
  // rendering for an arbitrary amount of time. The request remains queued and
  // is applied by the ordinary game loop after that modal work finishes. Close
  // this overlay now so it cannot prevent the player from reaching that point.
  settingsPanel?.close();
  $<HTMLCanvasElement>('#canvas').focus({ preventScroll: true });

  let waitingMessageShown = false;
  const waitingMessageAt = performance.now() + 1500;
  for (;;) {
    const state = status();
    if (state === 3) {
      activeGameDisplayMode = mode;
      activeHud?.setMode(mode);
      // The engine finishes rebuilding before the shell applies the final CSS
      // aspect-fit. Refresh its mouse transform after that layout commits.
      await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      module._rr_resize_presented?.();
      toast(`Resolution changed live to ${mode.width} × ${mode.height}.`, 'ok');
      return;
    }
    if (state < 0) {
      const message = `The engine could not apply the resolution (code ${state}).`;
      toast(message, 'bad');
      throw new Error(message);
    }
    if (!waitingMessageShown && performance.now() >= waitingMessageAt) {
      waitingMessageShown = true;
      toast('Resolution queued — it will change after the current action or screen closes.', 'warn', 6000);
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 50));
  }
}

async function startEngine(): Promise<void> {
  showScreen('play');
  const canvas = $<HTMLCanvasElement>('#canvas');
  const overlay = $('#boot-overlay');
  overlay.classList.remove('hidden');
  if (import.meta.env.DEV && isDevArena()) {
    $<HTMLElement>('#boot-overlay .brand h1').textContent = 'COMBAT LAB';
    $('#boot-stage').textContent = 'arming arena';
    $('#boot-detail').textContent = 'preparing live encounter';
  }

  const installMeta = await getMeta<InstallMeta>('install');
  const gameType: GameType = installMeta?.gameType ?? 'fo2';
  // The vault we are about to boot is the authority on which game's paths the
  // save archive, durability probe and item catalog should read. See config.ts.
  setRuntimeGame(gameType);
  const mode = await currentDisplayMode();
  activeGameDisplayMode = mode;

  const hud = mountHud(
    canvas,
    {
      onSaves: () => saveArchive.toggle('manage'),
      // Same overlay, edit-forward. The editor acts on a slot, so the slot list
      // is the entry point; what this changes is that it has one at all.
      onSaveEdit: () => saveArchive.toggle('edit'),
      onSettings: () => settingsPanel?.open(),
      onQuit: () => location.reload(),
      onFullscreenChange: (on) => {
        if (engineStopped) return;
        fullscreenPref = on;
        void setMeta('fullscreen', on);
      },
      onResizeAvailable: (target) => {
        if (engineStopped) return;
        // Portrait mobile owns a single adaptive framebuffer and independently
        // presents the map and stock HUD. Offering a legacy 640x480 "now fits"
        // resize here is both obsolete and disruptive during play (rotation,
        // browser chrome and keyboard changes can all retrigger it).
        if (matchMedia('(max-width: 600px) and (orientation: portrait)').matches) return;
        // The CSS scale has already been re-fitted; offer to rebuild the
        // framebuffer at the newly available size.
        toastAction(
          `${target.width} × ${target.height} now fits this screen.`,
          'Resize now',
          () => void applyLiveDisplayMode(target.id)
        );
      },
      portraitMode: () => engine?.module._rr_portrait_mode?.() ?? 0,
      onPointerEvent: (x, y, buttons) => {
        engine?.module._rr_pointer_event?.(x, y, buttons);
      },
      onInputModality: (touchLike) => {
        engine?.module._rr_input_modality?.(touchLike ? 1 : 0);
      },
      inCombat: () => engine?.module._rr_in_combat?.() === 1,
      indicatorCount: () => engine?.module._rr_indicator_count?.() ?? 0,
      mouseMode: () => engine?.module._rr_mouse_mode?.() ?? 0,
      isTouchInput: () => lastInputTouchLike,
      onNavigation: (target) => {
        engine?.module._rr_navigation_event?.(target);
      },
      characterSkillState: () => ({
        active: engine?.module._rr_character_editor_active?.() === 1,
        index: engine?.module._rr_character_skill_index?.() ?? -1,
        value: engine?.module._rr_character_skill_value?.() ?? -1,
        points: engine?.module._rr_character_unspent_skill_points?.() ?? -1,
      }),
      onCharacterSkillAdjust: (delta) => {
        engine?.module._rr_character_skill_adjust?.(delta);
      },
    },
    mode
  );
  activeHud = hud;

  const logEl = $('#boot-log');
  const logLines: string[] = [];
  let cfgText = await currentCfgText(gameType, mode);
  if (import.meta.env.DEV && isDevArena()) {
    cfgText = cfgText.includes('skip_opening_movies=')
      ? cfgText.replace(/skip_opening_movies=\d+/, 'skip_opening_movies=2')
      : cfgText.replace('[ui]', '[ui]\nskip_opening_movies=2');
  }

  try {
    let arenaSlot: number | null = null;
    engine = await boot({
      canvas,
      gameType,
      // The `import.meta.env.DEV` half is what lets rollup drop the dev source
      // (and its chunk) from a production build entirely.
      source:
        import.meta.env.DEV && (isDevDirectMount() || isDevArena())
          ? (await import('./dev/gamedata')).devEndpointSource
          : undefined,
      cfgText,
      // Kept in step with fallout2.cfg only so an imported Hi-Res art pack sees
      // a coherent file. The engine migrates f2_res.ini into fallout2.cfg only
      // when [screen] resolution_x is absent, and ours always sets it — so this
      // is inert for resolution and fallout2.cfg is the control. For FO1,
      // f1_res.ini IS the control: the engine sizes its window from it.
      f1ResText: gameType === 'fo1' ? f1ResIni(mode.width, mode.height) : undefined,
      f2ResText: gameType === 'fo2' ? f2ResIni(mode.width, mode.height) : undefined,
      onProgress: bootProgress,
      onLog: (line) => {
        // #boot-log is hidden unless boot fails, so also mirror to devtools —
        // that's where engine debugPrint output is useful during normal play.
        console.debug('[engine]', line);
        telemetry?.engineLog(line);
        logLines.push(line);
        if (logLines.length > 200) logLines.shift();
        logEl.textContent = logLines.join('\n');
      },
      onSync: () => {
        // Engine just persisted something — usually a save. Refresh the archive
        // if it happens to be open, and note the new state.
        const FS = getFS();
        if (FS) lastSlotsSignature = slotsSignature(FS);
        notifySavesChanged();
      },
      onExit: (code) => {
        toast(code === 0 ? 'The game exited.' : `The game exited with code ${code}.`, code === 0 ? 'ok' : 'bad');
        releaseFullscreen();
      },
      beforeMain: gameType === 'fo2' ? async (FS) => {
        const compiled = await applyRecipesToBoot(FS);
        if (compiled?.skipped.length) {
          toast(
            `${compiled.skipped.length} recipe mod(s) could not be applied — see the Mod Maker for details.`,
            'warn'
          );
        }
        if (import.meta.env.DEV && isDevArena()) {
          arenaSlot = await (await import('./dev/arena')).installDevArena(FS);
        }
      } : undefined,
      mainArgs: import.meta.env.DEV && isDevArena()
        ? [`--dev-load-game=${arenaSlot ?? 1}`]
        : undefined,
    });
  } catch (e) {
    // Emscripten's ErrnoError stringifies to nothing useful, so unpack it.
    const err = e as { message?: string; errno?: number; stack?: string; name?: string };
    const detail = [err?.name, err?.message, err?.errno !== undefined ? `errno=${err.errno}` : null]
      .filter(Boolean)
      .join(' ');
    console.error('[boot]', detail || e, err?.stack);
    logLines.push(`BOOT FAILED: ${detail}`, err?.stack ?? '');
    logEl.textContent = logLines.join('\n');
    $('#boot-stage').textContent = 'failed';
    $('#boot-detail').textContent = detail || String(e);
    logEl.hidden = false;
    // The fullscreen request rode the START click, before boot could fail — so
    // the failure log would otherwise be all there is to see, fullscreen.
    releaseFullscreen();
    return;
  }

  telemetry?.setEngine({ gameType, module: engine.module, mode });

  await setMeta('hasPlayed', true);
  if (import.meta.env.DEV && isDevArena()) {
    if (isDevSimulator()) {
      await (await import('./ui/simulator')).mountCombatSimulator(engine.module, engine.FS);
    } else {
      await (await import('./ui/arena')).mountArenaLab(engine.module, engine.FS);
    }
  }
  const saveTopology = resolveSaveTopology(engine.module);
  if (saveTopology) configureSaveTopology(saveTopology);
  if (recoverSaveTransactions(engine.FS)) await engine.sync();
  lastSlotsSignature = slotsSignature(engine.FS);
  notifySavesChanged();
  engine.module._rr_touch_assist_scale?.(touchAssistScale);
  // The engine's naming prompts are drawn into the canvas, so nothing is
  // focused and a phone would never raise its keyboard for them.
  if (!textEntry) {
    textEntry = mountTextEntry({
      active: () => engine?.module._rr_text_input_active?.() === 1,
      prefill: () => readTextInputPrefill(),
      isTouch: () => lastInputTouchLike,
      sendKey: (k) => engine?.module._rr_key_event?.(k) ?? -99,
    });
  }
  startTouchPreview(canvas);
  startHitboxDebug(canvas);

  const activeCanvasTouches = new Map<number, { clientX: number; clientY: number; x: number; y: number }>();
  let pinchCandidateDistance = 0;
  let pinchCandidateCenter = { x: 0, y: 0 };
  let pinchLastDistance = 0;
  let pinchLastCenter = { x: 0, y: 0 };
  let pinchActive = false;
  let consumePinchRemainder = false;
  let pinchFrame = 0;
  type MapGesture = {
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
    startedAt: number;
    mode: 'candidate' | 'pan' | 'hold' | 'pinch';
    holdTimer?: number;
  };
  const mapGestures = new Map<number, MapGesture>();
  const clearMapHold = (gesture?: MapGesture) => {
    if (gesture?.holdTimer !== undefined) window.clearTimeout(gesture.holdTimer);
    if (gesture) gesture.holdTimer = undefined;
  };
  const firstTwoTouchDistance = () => {
    const points = [...activeCanvasTouches.values()];
    return points.length >= 2
      ? Math.hypot(points[0].clientX - points[1].clientX, points[0].clientY - points[1].clientY)
      : 0;
  };
  const firstTwoTouchCenter = () => {
    const points = [...activeCanvasTouches.values()];
    return points.length >= 2
      ? { x: (points[0].clientX + points[1].clientX) / 2, y: (points[0].clientY + points[1].clientY) / 2 }
      : { x: 0, y: 0 };
  };

  // Every touch phase the engine has actually been told about, with the last
  // coordinates we sent for it. The shell withholds, replays and rewrites
  // touches, so "does the engine believe this pointer is down, and where?" is
  // not derivable from the DOM events alone — it has to be recorded here. A
  // release we cannot match to a recorded down is never sent, and a down we
  // cannot match to a release is always closed out during reconciliation.
  const engineTouchDown = new Map<number, { x: number; y: number }>();
  const sendTouch = (x: number, y: number, pointerId: number, phase: number, timestamp: number) => {
    const touchEvent = engine?.module._rr_touch_event;
    if (!touchEvent) return;
    touchEvent(x, y, pointerId, phase, timestamp);
    if (phase === 0) {
      engineTouchDown.set(pointerId, { x, y });
    } else if (phase === 1) {
      const point = engineTouchDown.get(pointerId);
      if (point) {
        point.x = x;
        point.y = y;
      }
    } else {
      engineTouchDown.delete(pointerId);
    }
  };
  const cancelAllEngineTouches = () => {
    engine?.module._rr_touch_cancel_all?.();
    engineTouchDown.clear();
  };
  // Contacts that were still down when the presentation mode flipped. Their
  // gesture was resolved and closed by the reconcile below, so the rest of
  // their DOM sequence must not re-enter either recognizer; they are simply
  // ignored until the finger lifts.
  const strandedPointers = new Set<number>();

  const resetPinchState = () => {
    pinchActive = false;
    consumePinchRemainder = false;
    pinchCandidateDistance = 0;
    pinchCandidateCenter = { x: 0, y: 0 };
    pinchLastDistance = 0;
    pinchLastCenter = { x: 0, y: 0 };
    if (pinchFrame) cancelAnimationFrame(pinchFrame);
    pinchFrame = 0;
  };

  // One pinch step per animation frame, never per pointer event. A two-finger
  // move reaches the DOM as two pointermoves, one per contact, so between them
  // the spread is transiently wrong: a constant-spread drag reads as widen-
  // then-narrow. Applied step by step against a clamped zoom those two halves
  // no longer cancel (the widen is swallowed at the cap, the narrow is not),
  // and the scale drifts. Worse, the transient could clear the pinch threshold
  // and steal an honest two-finger pan from the engine. By the time a frame
  // callback runs, both contacts have reported for this input batch.
  const runPinchFrame = () => {
    pinchFrame = 0;
    if (activeCanvasTouches.size < 2) return;
    const distance = firstTwoTouchDistance();
    const center = firstTwoTouchCenter();
    if (!pinchActive) {
      if (!(pinchCandidateDistance > 0 && Math.abs(distance - pinchCandidateDistance) >= 10)) return;
      // Scale intent is clear. On the portrait map the presenter zooms the
      // world pane; elsewhere the HUD magnifies the canvas itself. Either
      // way the engine must not keep seeing the two contacts as a pan, so
      // cancel them before the first zoom step.
      const zoomed = activeHud?.transformMap(
        distance / pinchCandidateDistance,
        pinchCandidateCenter.x,
        pinchCandidateCenter.y,
        center.x,
        center.y
      ) ?? false;
      const mapActive = $('#game-presentation').classList.contains('mobile-map-active');
      if (!zoomed && !mapActive) {
        // Nothing can zoom here (a stock modal, say). Leave the contacts to
        // the engine's own two-finger gestures and stop re-testing them.
        pinchCandidateDistance = 0;
        return;
      }
      cancelAllEngineTouches();
      for (const gesture of mapGestures.values()) {
        clearMapHold(gesture);
        gesture.mode = 'pinch';
      }
      pinchActive = true;
      consumePinchRemainder = true;
    } else if (distance > 0 && pinchLastDistance > 0) {
      activeHud?.transformMap(
        distance / pinchLastDistance,
        pinchLastCenter.x,
        pinchLastCenter.y,
        center.x,
        center.y
      );
    }
    pinchLastDistance = distance;
    pinchLastCenter = center;
  };

  // The shell routes touches by a DOM class that the engine's real screen mode
  // drives. A gesture that straddles a flip belongs to neither recognizer: the
  // map resolver may be holding a withheld DOWN, or the engine may be holding
  // a DOWN whose UP the map resolver would swallow. Rather than patch each
  // straddle case, close the whole world out on every transition — release
  // anything the engine believes is held, drop all shell-side gesture state,
  // and strand the live contacts. Idempotent: with nothing outstanding it is a
  // handful of empty-collection checks.
  const reconcileModeFlip = () => {
    const now = Math.round(performance.now());
    for (const gesture of mapGestures.values()) clearMapHold(gesture);
    mapGestures.clear();
    for (const [pointerId, point] of [...engineTouchDown]) {
      // Phase 3 (cancel), not 2 (up): an UP replayed here can still satisfy the
      // engine's tap branch - it has no maximum single-finger press duration -
      // and fire a ghost click on the screen the flip just opened. Cancel takes
      // the same `touch_handle_end` path but marks the contact canceled, which
      // the tap branch refuses outright. `sendTouch` drops the map entry for
      // phase 3 exactly as it does for 2.
      sendTouch(point.x, point.y, pointerId, 3, now);
    }
    for (const pointerId of activeCanvasTouches.keys()) strandedPointers.add(pointerId);
    activeCanvasTouches.clear();
    resetPinchState();
  };

  // Watch the class itself rather than any one call site: this catches the
  // presenter's setMode toggle, its teardown, and anything else that could
  // ever move the shell between routing modes. classList.toggle with an
  // unchanged state writes no attribute, so an idle frame costs nothing.
  const presentationEl = $('#game-presentation');
  let lastMapPresentationActive = presentationEl.classList.contains('mobile-map-active');
  new MutationObserver(() => {
    const active = presentationEl.classList.contains('mobile-map-active');
    if (active === lastMapPresentationActive) return;
    lastMapPresentationActive = active;
    reconcileModeFlip();
  }).observe(presentationEl, { attributes: true, attributeFilter: ['class'] });

  const forwardPointer = (event: MouseEvent | PointerEvent) => {
    const touchLike = 'pointerType' in event && event.pointerType !== 'mouse';
    if ('pointerType' in event) {
      lastInputTouchLike = touchLike;
      engine?.module._rr_input_modality?.(touchLike ? 1 : 0);
    } else {
      // WheelEvent has no pointerType and is mouse/trackpad input.
      lastInputTouchLike = false;
      engine?.module._rr_input_modality?.(0);
    }
    if (touchLike) {
      const touchEvent = engine?.module._rr_touch_event;
      if (!touchEvent) return;
      const rect = canvas.getBoundingClientRect();
      const x = Math.round(((event.clientX - rect.left) / rect.width) * 1_000_000);
      let y = Math.round(((event.clientY - rect.top) / rect.height) * 1_000_000);
      const mapPresentationActive = $('#game-presentation').classList.contains('mobile-map-active');
      // Gesture recognition must be stable in physical screen space. Pinch
      // changes the canvas CSS scale, so a fixed game-pixel threshold makes
      // pans progressively harder as the map zooms in. Convert a comfortable
      // 7 CSS-pixel stroke into current logical game pixels.
      const panThreshold = mapPresentationActive && activeGameDisplayMode && rect.width > 0
        ? Math.max(3, Math.min(20, Math.round(7 * activeGameDisplayMode.width / rect.width)))
        : 0;
      engine?.module._rr_touch_pan_threshold?.(panThreshold);
      // The source framebuffer still contains the stock 100px HUD even though
      // CSS clips it and the mobile mirror is the only visible copy. Safari
      // pointer capture can keep routing a map gesture through that clipped
      // geometry. Never allow the map canvas to address the ghost HUD strip;
      // the separate mobile HUD canvas owns those controls.
      //
      // Gated on the portrait media query, NOT on mobile-map-active: the
      // presentation class flips off while a stock modal (skilldex, options)
      // is up and takes a poll tick to flip back after it closes. A tap in
      // that window - lockpick armed, door tapped promptly - used to map into
      // the hidden bar band and die on an invisible bar button. In portrait
      // the mirror is always the bar's input surface, so the canvas never
      // legitimately addresses the bar at all while it is visible.
      if (activeGameDisplayMode
        && matchMedia('(max-width: 600px) and (orientation: portrait)').matches
        && engine?.module._rr_interface_bar_visible?.() === 1) {
        const worldBottom = Math.max(0, activeGameDisplayMode.height - 100 - 1);
        y = Math.min(y, Math.floor(worldBottom * 1_000_000 / activeGameDisplayMode.height));
      }
      const phase = event.type === 'pointerdown' ? 0 : event.type === 'pointermove' ? 1 : event.type === 'pointercancel' ? 3 : 2;
      if (event.type === 'pointerdown') canvas.setPointerCapture(event.pointerId);
      if ((event.type === 'pointerup' || event.type === 'pointercancel') && canvas.hasPointerCapture(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }

      if (HITBOX_DEBUG) {
        touchDebugPointers.unshift({ phase: ['DOWN', 'MOVE', 'UP', 'CANCEL'][phase] ?? `P${phase}`, x: Math.round(x / 1000), y: Math.round(y / 1000), at: performance.now() });
        if (touchDebugPointers.length > 12) touchDebugPointers.pop();
      }

      if (event.type === 'pointerdown') {
        // Pointer ids are recycled, so a fresh contact always outranks a
        // stranded one carrying the same id.
        strandedPointers.delete(event.pointerId);
      } else if (strandedPointers.has(event.pointerId)) {
        if (event.type === 'pointerup' || event.type === 'pointercancel') {
          strandedPointers.delete(event.pointerId);
          activeCanvasTouches.delete(event.pointerId);
        }
        return;
      }

      if (event.type === 'pointerdown' || event.type === 'pointermove') {
        activeCanvasTouches.set(event.pointerId, { clientX: event.clientX, clientY: event.clientY, x, y });
      }

      // The portrait map uses one authoritative DOM gesture resolver. Fallout
      // receives nothing at touchdown; only the resolved outcome is forwarded.
      // This makes tap, pan, pinch and hold mutually exclusive by construction.
      if (mapPresentationActive && event.type === 'pointerdown') {
        const gesture: MapGesture = {
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: x,
          startY: y,
          currentX: x,
          currentY: y,
          startedAt: Math.round(performance.now()),
          mode: 'candidate',
        };
        mapGestures.set(event.pointerId, gesture);
        gesture.holdTimer = window.setTimeout(() => {
          gesture.holdTimer = undefined;
          if (gesture.mode !== 'candidate' || mapGestures.get(event.pointerId) !== gesture) return;
          // Half a second is long enough for the screen mode to have changed
          // underneath this contact. Releasing the withheld DOWN into a screen
          // the player is no longer looking at is exactly the wedge this timer
          // used to create, so re-check the live mode instead of the one that
          // was current when the finger landed.
          if (!$('#game-presentation').classList.contains('mobile-map-active')) {
            mapGestures.delete(event.pointerId);
            strandedPointers.add(event.pointerId);
            activeCanvasTouches.delete(event.pointerId);
            return;
          }
          if (mapGestures.size !== 1) return;
          gesture.mode = 'hold';
          sendTouch(gesture.startX, gesture.startY, event.pointerId, 0, gesture.startedAt);
        }, 500);
      }

      if (event.type === 'pointerdown' && activeCanvasTouches.size === 2 && mapPresentationActive) {
        pinchCandidateDistance = firstTwoTouchDistance();
        pinchCandidateCenter = firstTwoTouchCenter();
        // Ownership changes the instant a second finger lands. Waiting for a
        // scale delta before canceling let the legacy engine observe the
        // candidate interval; a short/staggered pinch could then leave one
        // stationary tap queued and walk in combat. Keep the visual zoom
        // threshold, but make the entire two-finger sequence presenter-only.
        consumePinchRemainder = true;
        for (const gesture of mapGestures.values()) {
          clearMapHold(gesture);
          gesture.mode = 'pinch';
        }
        cancelAllEngineTouches();
        return;
      } else if (event.type === 'pointerdown' && activeCanvasTouches.size === 2) {
        // Outside the portrait map (an iPad, a phone held sideways) both downs
        // go straight to the engine: a two-finger tap is its right click and
        // must stay one. Only remember the spread so a later scale change can
        // still be recognised as a pinch.
        pinchCandidateDistance = firstTwoTouchDistance();
        pinchCandidateCenter = firstTwoTouchCenter();
      }

      if (event.type === 'pointermove'
        && activeCanvasTouches.size >= 2
        && (pinchActive || (pinchCandidateDistance > 0 && activeCanvasTouches.size === 2))) {
        if (!pinchFrame) pinchFrame = requestAnimationFrame(runPinchFrame);
        // Until the frame decides otherwise a candidate pair is still the
        // engine's (a two-finger pan, or on the portrait map a withheld
        // sequence the remainder gate already covers). A live pinch is ours.
        if (pinchActive) return;
      }

      if (mapPresentationActive && event.type === 'pointerdown') {
        // A lone touchdown remains only a candidate. It is deliberately not
        // forwarded until move/hold/release resolves the gesture below.
        return;
      }

      if (mapPresentationActive && event.type === 'pointermove' && !consumePinchRemainder) {
        const gesture = mapGestures.get(event.pointerId);
        if (!gesture) return;
        gesture.currentX = x;
        gesture.currentY = y;
        const distance = Math.hypot(event.clientX - gesture.startClientX, event.clientY - gesture.startClientY);
        if ((gesture.mode === 'candidate' || gesture.mode === 'hold') && distance >= 7) {
          const wasForwarded = gesture.mode === 'hold';
          clearMapHold(gesture);
          gesture.mode = 'pan';
          if (!wasForwarded) {
            sendTouch(gesture.startX, gesture.startY, event.pointerId, 0, gesture.startedAt);
          }
        }
        if (gesture.mode === 'pan' || gesture.mode === 'hold') {
          sendTouch(x, y, event.pointerId, 1, Math.round(performance.now()));
        }
        return;
      }

      if (mapPresentationActive && (event.type === 'pointerup' || event.type === 'pointercancel')) {
        const gesture = mapGestures.get(event.pointerId);
        activeCanvasTouches.delete(event.pointerId);
        if (consumePinchRemainder) {
          clearMapHold(gesture);
          mapGestures.delete(event.pointerId);
          if (activeCanvasTouches.size === 0) resetPinchState();
          return;
        }
        if (!gesture) {
          // The contact began before the map took over routing, so the engine
          // already holds a raw DOWN for it. Dropping this release, as we used
          // to, left that DOWN held forever and wedged the recognizer; forward
          // the matching release raw instead.
          if (engineTouchDown.has(event.pointerId)) {
            sendTouch(x, y, event.pointerId, phase, Math.round(performance.now()));
          }
          return;
        }
        clearMapHold(gesture);
        mapGestures.delete(event.pointerId);
        if (event.type === 'pointercancel') {
          if (engineTouchDown.has(event.pointerId)) {
            sendTouch(x, y, event.pointerId, 3, Math.round(performance.now()));
          }
          return;
        }
        if (gesture.mode === 'candidate') {
          // One resolved tap, aimed where the finger landed. Finger-roll at
          // release cannot retarget it and no pan samples have reached Fallout.
          sendTouch(gesture.startX, gesture.startY, event.pointerId, 0, gesture.startedAt);
          sendTouch(gesture.startX, gesture.startY, event.pointerId, 2, Math.round(performance.now()));
        } else if (engineTouchDown.has(event.pointerId)) {
          sendTouch(x, y, event.pointerId, 2, Math.round(performance.now()));
        }
        return;
      }

      if (event.type === 'pointerup' || event.type === 'pointercancel') {
        // A gesture that started on the map and ends here left an entry (and
        // possibly a hold timer) behind. Retire it on the way out whatever the
        // current mode is, or the map resolver keeps stale contacts forever.
        const gesture = mapGestures.get(event.pointerId);
        if (gesture) {
          clearMapHold(gesture);
          mapGestures.delete(event.pointerId);
        }
        activeCanvasTouches.delete(event.pointerId);
        if (consumePinchRemainder) {
          // The pinch is over as far as the shell is concerned, but the gate
          // stays shut until the last finger lifts. Reopening it here is what
          // stops a mode change mid-pinch from swallowing all later input.
          if (activeCanvasTouches.size === 0) resetPinchState();
          return;
        }
        // Never release a contact the engine was never told about: a withheld
        // candidate DOWN has no matching UP to send.
        if (!engineTouchDown.has(event.pointerId)) return;
        sendTouch(x, y, event.pointerId, phase, Math.round(performance.now()));
        return;
      }

      // This includes the pre-threshold pinch candidate and the surviving
      // finger after a staggered lift. Neither may re-enter legacy touch.
      if (consumePinchRemainder) return;

      if (activeCanvasTouches.size > 2) {
        // Preserve the engine's existing three/four-finger gestures.
        pinchCandidateDistance = 0;
      }
      sendTouch(x, y, event.pointerId, phase, Math.round(performance.now()));
      return;
    }
    const pointerEvent = engine?.module._rr_pointer_event;
    if (pointerEvent) {
      const rect = canvas.getBoundingClientRect();
      const x = Math.round(((event.clientX - rect.left) / rect.width) * 1_000_000);
      const y = Math.round(((event.clientY - rect.top) / rect.height) * 1_000_000);
      // DOM buttons: bit 0 left, bit 1 right. The engine bridge intentionally
      // accepts that compact layout rather than SDL's sparse button mask.
      pointerEvent(x, y, event.buttons & 3);
    }
  };
  canvas.addEventListener('pointermove', forwardPointer, { passive: true });
  canvas.addEventListener('pointerdown', forwardPointer, { passive: true });
  canvas.addEventListener('pointerup', forwardPointer, { passive: true });
  canvas.addEventListener('pointercancel', forwardPointer, { passive: true });
  // Capture on window, before Emscripten's target listener. preventDefault
  // cancels only Chrome's page scrolling; propagation continues, so SDL still
  // receives the wheel event and inventory/loot/barter can scroll the pane
  // beneath the game cursor. Forward the wheel coordinates too, since a wheel
  // can be the first event after a resize or tab focus change.
  window.addEventListener('wheel', (event) => {
    if (!event.composedPath().includes(canvas)) return;
    event.preventDefault();
    forwardPointer(event);
  }, { capture: true, passive: false });

  // callMain returns immediately under asyncify. Ordinary play gets a short
  // presentation delay; the arena keeps the curtain up until the save has
  // actually become a live engine session. That hides the stock title/load
  // windows used internally by the reliable autoload path and avoids revealing
  // a black framebuffer when large data ranges are still arriving.
  if (import.meta.env.DEV && isDevArena()) {
    $('#boot-stage').textContent = 'entering arena';
    $('#boot-detail').textContent = 'loading encounter';
    const revealArena = (): void => {
      if (engine?.module._rr_game_active?.() === 1) {
        requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.add('hidden')));
        return;
      }
      setTimeout(revealArena, 50);
    };
    revealArena();
  } else {
    // Give SDL a beat to present the first frame before pulling the curtain.
    setTimeout(() => overlay.classList.add('hidden'), 900);
  }

  watchSaves();
  installHotkeys();
  window.addEventListener('beforeunload', () => {
    engine?.sync().catch(() => {});
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      engine?.sync().catch(() => {});
      return;
    }
    // SDL sizes its drawing buffer from the container's client box. A hidden
    // tab reports 0x0, so an engine-side window rebuild while backgrounded can
    // leave the canvas collapsed. Nudge SDL once we are visible again.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      hud.fit();
    });
  });
}

/**
 * Belt and braces on top of the engine's own syncfs: quicksaves inside the
 * engine already trigger one, but a shell that only ever learns about saves
 * through the engine would miss anything written by a code path we have not
 * inspected. Polling a cheap stat signature costs nothing at 4s intervals.
 */
function watchSaves(): void {
  setInterval(() => {
    const FS = getFS();
    if (!FS) return;
    const sig = slotsSignature(FS);
    if (sig !== lastSlotsSignature) {
      lastSlotsSignature = sig;
      notifySavesChanged();
    }
  }, 4000);
}

// ------------------------------------------------------------------ hotkeys

function installHotkeys(): void {
  window.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'F9') {
        e.preventDefault();
        e.stopPropagation();
        saveArchive.toggle('manage');
      } else if (e.key === 'Escape' && saveEditor?.isOpen) {
        e.preventDefault();
        e.stopPropagation();
        // requestClose, not close: Escape is the shortcut a player reaches for
        // by reflex, and it must not be the one route that discards unwritten
        // edits without asking.
        saveEditor.requestClose();
      } else if (e.key === 'Escape' && saveArchive.isOpen) {
        e.preventDefault();
        e.stopPropagation();
        saveArchive.close();
      }
    },
    // Capture phase: SDL installs its own window-level key handler and would
    // otherwise swallow F9 before the shell ever sees it.
    true
  );
}

// --------------------------------------------------------------------- init

function gameLabel(entry: GameEntry): string {
  if (entry.gameType === 'fo1') return 'Fallout';
  return entry.hasRpu ? 'Fallout 2 · RPU' : 'Fallout 2';
}

function crtArt(entry: GameEntry): string {
  if (entry.gameType === 'fo1') return '/media/crt-fallout-1.jpg';
  return entry.hasRpu ? '/media/crt-fallout-2-rpu.jpg' : '/media/crt-fallout-2.jpg';
}

function renderGamePicker(entries: GameEntry[], active: GameType): void {
  const picker = $('#game-picker');
  picker.hidden = false;
  picker.replaceChildren();
  for (const entry of entries) {
    const label = gameLabel(entry);
    const btn = document.createElement('button');
    btn.className = 'game-tile';
    btn.setAttribute('aria-pressed', entry.gameType === active ? 'true' : 'false');
    btn.setAttribute('aria-label', label);
    btn.title = label;

    const img = document.createElement('img');
    img.src = crtArt(entry);
    img.alt = label;
    img.draggable = false;
    btn.appendChild(img);

    // Visible-to-screen-readers-only caption: the art carries the name, but the
    // button should still announce and search as text.
    const caption = document.createElement('span');
    caption.className = 'visually-hidden';
    caption.textContent = label;
    btn.appendChild(caption);

    btn.addEventListener('click', () => {
      if (entry.gameType === active) return;
      setActiveGame(entry.gameType);
      location.reload();
    });
    picker.appendChild(btn);
  }
  if (entries.length < 3) {
    const addBtn = document.createElement('button');
    addBtn.className = 'game-tile game-tile-add';
    addBtn.title = 'Install another game';
    addBtn.setAttribute('aria-label', 'Add a game');

    const plus = document.createElement('span');
    plus.className = 'game-tile-plus';
    plus.textContent = '+';
    plus.setAttribute('aria-hidden', 'true');
    addBtn.appendChild(plus);

    const text = document.createElement('span');
    text.className = 'game-tile-add-label';
    text.textContent = 'Add a game';
    addBtn.appendChild(text);

    addBtn.addEventListener('click', () => showScreen('landing'));
    picker.appendChild(addBtn);
  }
}

async function showGate(meta: InstallMeta): Promise<void> {
  showScreen('gate');
  const gameType: GameType = meta.gameType ?? 'fo2';
  // The archive is reachable from the gate, before boot, so pin the game here
  // too rather than only in startEngine().
  setRuntimeGame(gameType);
  const isFo1 = gameType === 'fo1';
  const rpu = isFo1 ? null : await getRpuMeta();

  const registry = readRegistry();
  if (registry.length > 0) renderGamePicker(registry, gameType);

  const parts = [
    `${meta.fileCount} files, ${bytes(meta.totalBytes)} installed`,
  ];
  if (isFo1) {
    parts.push('Fallout 1');
  } else {
    parts.push(
      rpu
        ? `Restoration Project${rpu.version === 'unknown' ? '' : ` v${rpu.version}`}`
        : meta.hasPatch
          ? 'version 1.02'
          : 'version 1.0 — unpatched'
    );
  }
  if (meta.source === 'cd') parts.push('from disc image');
  $('#gate-summary').textContent = parts.join(' · ');
  // FO2-only UI: patch badge, RPU button, Mod Maker button
  $('#gate-patch-badge').hidden = isFo1 || meta.hasPatch || !!rpu;
  $('#btn-gate-rpu').hidden = isFo1;
  $('#btn-gate-modmaker').hidden = isFo1;
}

/** Re-reads install meta so the gate reflects a patch added moments ago. */
async function refreshGate(): Promise<void> {
  const meta = await getMeta<InstallMeta>('install');
  if (meta) await showGate(meta);
}

async function init(): Promise<void> {
  installPwaMetadata();
  // Before anything else, so a boot that fails is still described. Reads
  // ?telemetry=1 / localStorage itself; F9 turns it on mid-session.
  telemetry = mountTelemetry({ canvas: $<HTMLCanvasElement>('#canvas') });

  // --- Resolve active vault before any getMeta call ---
  if (isDevArena() || isDevDirectMount()) {
    // These two dev modes bypass the registry entirely and mount Fallout 2 from
    // the local endpoint. Say so, or a stale `f2ce-active` from a Fallout 1
    // session would point the save archive at /fallout1-ce.
    setRuntimeGame('fo2');
  } else {
    let registry = readRegistry();
    if (registry.length === 0) {
      useVault('f2ce-shell');
      const legacyMeta = await getMeta<InstallMeta>('install');
      if (legacyMeta) {
        const game: GameType = legacyMeta.gameType ?? 'fo2';
        const rpu = game === 'fo2' ? await getRpuMeta() : null;
        registerGame({
          gameType: game,
          dbName: 'f2ce-shell',
          installedAt: legacyMeta.installedAt,
          totalBytes: legacyMeta.totalBytes,
          fileCount: legacyMeta.fileCount,
          hasRpu: !!rpu,
          rpuVersion: rpu && rpu.version !== 'unknown' ? rpu.version : undefined,
        });
        registry = readRegistry();
      }
    }
    if (registry.length > 0) {
      const preferred = getActiveGame();
      const entry = (preferred && registry.find((e) => e.gameType === preferred)) ?? registry[0];
      setActiveGame(entry.gameType);
      useVault(entry.dbName);
    }
  }

  fullscreenPref = (await getMeta<boolean>('fullscreen')) ?? false;
  touchAssistScale = (await getMeta<number>('touchAssistScale')) ?? 100;
  if (isDevArena()) {
    document.body.classList.add('arena-mode');
    $('#btn-start').textContent = 'ENTER ARENA';
  }

  $('#btn-start').addEventListener('click', () => {
    // Fullscreen needs a user gesture and the engine needs its framebuffer
    // decided before boot; both hang off this one click. The request goes
    // first, synchronously, so the activation is still transient.
    if (fullscreenPref && !document.fullscreenElement) {
      void document.documentElement.requestFullscreen?.().catch(() => {});
    }
    void startEngine();
  });
  $('#btn-manage').addEventListener('click', () => managePanel.open());
  $('#btn-gate-saves').addEventListener('click', () => saveArchive.open());

  settingsPanel = mountSettings(
    (await getMeta<string>('displayMode')) ?? DEFAULT_DISPLAY_MODE,
    touchAssistScale,
    async (id) => void (await setMeta('displayMode', id)),
    applyLiveDisplayMode,
    async (scale) => {
      touchAssistScale = scale;
      await setMeta('touchAssistScale', scale);
      engine?.module._rr_touch_assist_scale?.(scale);
    }
  );

  // Before anything can repopulate either store: the canary compares what used
  // to be here against what is, and that comparison is only meaningful while
  // "what is here" is still whatever the browser left us.
  const storage = await assessStorage();
  // What is on disk now, not what the canary remembers: after a wipe the
  // remembered count is exactly the wrong answer. A null read means the archive
  // could not be opened, and an unread archive earns no home-screen nudge.
  hasSaves = ((await countPersistedSlots()) ?? 0) > 0;
  a2hsChip = mountA2hs(() => hasSaves);
  // Recovery is offered two ways, because detection is best-effort: a notice
  // when the canary is certain, and a lane on the import screen that is always
  // there for when it is not.
  const openIntake = () => window.dispatchEvent(new CustomEvent('rerun:open-intake', { detail: { mode: 'reimport' } }));
  mountRestoreLane(() => notifySavesChanged());
  window.addEventListener('rerun:open-intake', () => mountRestoreLane(() => notifySavesChanged()));
  if (storage.state === 'evicted') {
    console.warn('[storage] eviction detected', storage);
    mountRecoveryNotice(openIntake);
  }

  // MEMFS is rebuilt from the vault on every boot, so anything that should
  // never have been vaulted is not a one-off mistake — it is reapplied for
  // ever. Evict it before the engine can read it. See shell/README.md.
  const purged = await purgeContamination();
  if (purged.length) {
    console.info('[vault] purged mod-loader/migrated config:', purged);
    toast(`Removed ${purged.length} stale mod-loader config file(s) from the vault.`, 'warn', 6000);
  }

  patchPanel = mountPatchPanel(
    () => {
      void refreshGate();
      modMaker?.invalidateBase();
    },
    () => void acknowledgeUnpatched()
  );
  $('#gate-patch-badge').addEventListener('click', () => patchPanel?.open());

  rpuPanel = mountRpuPanel(() => {
    void refreshGate();
    // The base a recipe compiles against just changed shape.
    modMaker?.invalidateBase();
  });
  $('#btn-gate-rpu').addEventListener('click', () => rpuPanel?.open());

  modMaker = mountModMaker({ isBooted: () => !!engine });
  $('#btn-gate-modmaker').addEventListener('click', () => modMaker?.open());

  mountInstaller(async (result) => {
    window.dispatchEvent(new Event('rerun:close-intake'));
    modMaker?.invalidateBase();
    await showGate(result.meta);
    // A disc is v1.0 by definition, so the patch offer is part of the CD import
    // rather than a thing to go looking for. Shown once per import; after that
    // the gate badge is the standing reminder. A Restoration Project install
    // has no patch000.dat *by design*, so it earns no patch nag.
    if (result.meta.gameType !== 'fo1' && !result.meta.hasPatch && !(await getRpuMeta()) && !(await isUnpatchedAcknowledged())) patchPanel?.open();
  }, () => notifySavesChanged());

  if (import.meta.env.DEV) {
    // Exposes window.__shot(name) for writing documentation screenshots.
    await import('./dev/shot');
    // Exposes window.__fixtureDrop(...) so the disc importer can be driven
    // against a real CD image without a human to drag it.
    await import('./dev/fixtures');
    // Exposes window.__itemCatalog() so the Inventory tab's catalog can be
    // asserted on straight off the vault, with no boot and no save slot.
    await import('./dev/catalog');
    // Exposes window.__engineFS/__engineModule/__seedFile/__readFile so a real
    // native save slot can be planted where the save editor will find it, and
    // the bridge exports can be called (and removed) independently of the shell.
    const devFs = await import('./dev/fs');
    devFs.installFsHook(getFS, () => engine?.module ?? null);
    // Lets the e2e observe the post-bridge restart instead of being reloaded
    // out of its own assertions. Real runs are unaffected.
    restartEngine = devFs.devRestart(restartEngine);
  }

  if (import.meta.env.DEV && (isDevGameData() || isDevDirectMount() || isDevArena())) {
    const badge = document.createElement('div');
    badge.className = 'dev-badge';
    badge.textContent = isDevArena()
      ? 'DEV — CE combat arena'
      : isDevDirectMount() ? 'DEV — direct mount, no vault' : 'DEV — local game data endpoint';
    document.body.appendChild(badge);
  }

  if (isDevArena()) {
    void startEngine();
    return;
  }

  if (isDevDirectMount()) {
    // No vault, so no install record to describe. Straight to the gate.
    showScreen('gate');
    const arenaEnemy = new URLSearchParams(location.search).get('enemy') === 'deathclaw' ? 'deathclaw' : 'raider';
    $('#gate-summary').textContent = isDevArena()
      ? `CE arena: one stock desert encounter, one real ${arenaEnemy}, nothing stored`
      : 'dev: mounting from the local endpoint, nothing stored';
    $('#gate-patch-badge').hidden = true;
    return;
  }

  const meta = await getMeta<InstallMeta>('install');
  if (meta) {
    await showGate(meta);
    return;
  }

  showScreen('landing');
  if (import.meta.env.DEV && isDevGameData()) {
    // Saves re-dragging half a gigabyte on every reload during development.
    toast('Dev: importing game data from the local endpoint…', 'warn', 6000);
    $<HTMLButtonElement>('#btn-dev-import').click();
  }
}

void init();
